import axiosClient from "../axiosClient";
import {
  balanceAfterReward,
  persistEasterBalance,
  readEasterBalance,
} from "../economy/api_v1_economy";
import {
  isLoginChallenge,
  loadChallenges,
  refreshChallenges,
} from "../challenges/api_v1_challenges";
import {
  announceEggReward,
  claimedRewardIsNew,
  hasRewardBaseline,
  rememberClaimedRewards,
  rewardTaskId,
} from "../../utils/eggReward";
import {
  CACHE_KEYS,
  SESSION_CACHE_TTL,
  cachedRequest,
  invalidateCachedRequest,
} from "../../utils/apiCache";

const WEEK_LENGTH = 7;
/** Streak box labels cycle 1 → 30, then restart at 1. */
const STREAK_CYCLE_LENGTH = 30;
/** 0-based index of the third-last box (box 5 of 7) where active locks from day 6+. */
const ACTIVE_LOCK_SLOT = 4;

function withBearer() {
  const bearerTokenHeader = sessionStorage.getItem("accessToken");
  axiosClient.defaults.headers.common["Authorization"] =
    `Bearer ${bearerTokenHeader}`;
  return axiosClient;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function firstFinite(...values) {
  for (const value of values) {
    if (value == null || value === "") continue;
    const n = Number(value);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return null;
}

function unwrap(payload) {
  if (!payload || typeof payload !== "object") return null;
  const nested = payload.data;
  if (
    nested &&
    typeof nested === "object" &&
    !Array.isArray(nested) &&
    (nested.active_streak != null ||
      nested.reward_status != null ||
      nested.streak_to_claim != null ||
      nested.amount_to_claim != null)
  ) {
    return {
      ...nested,
      success: payload.success !== false && nested.success !== false,
      message: nested.message ?? payload.message,
    };
  }
  if (
    payload.active_streak == null &&
    nested &&
    typeof nested === "object" &&
    !Array.isArray(nested)
  ) {
    return nested;
  }
  return payload;
}

function cycleDay(day) {
  const n = Math.max(1, toNumber(day, 1));
  return ((n - 1) % WEEK_LENGTH) + 1;
}

export function getActiveStreak(options = {}) {
  return cachedRequest(
    CACHE_KEYS.activeStreak,
    () => withBearer().get("/active-streak"),
    { ttl: SESSION_CACHE_TTL, force: options.force === true },
  );
}

export function updateDailyStreak() {
  invalidateCachedRequest(CACHE_KEYS.activeStreak);
  return withBearer().post("/update-daily-streak", {});
}

export function normalizeActiveStreak(payload) {
  const root = unwrap(payload);
  if (!root || root.success === false) return null;

  const activeStreak = Math.max(0, toNumber(root.active_streak, 0));
  const streakToClaim = Math.max(
    1,
    toNumber(root.streak_to_claim, activeStreak + 1)
  );
  const amountToClaim = Math.max(0, toNumber(root.amount_to_claim, 1));
  const rewardClaimed = Boolean(root.rewardClaimed ?? root.reward_claimed);
  const focus = rewardClaimed ? Math.max(activeStreak, 1) : streakToClaim;

  return {
    success: true,
    activeStreak,
    streakToClaim,
    amountToClaim,
    rewardClaimed,
    displayDay: cycleDay(focus),
    nextDay: cycleDay(focus + 1),
    eggBalance: null,
    eggsAwarded: amountToClaim,
    message: root.message || "",
  };
}

export function normalizeUpdatedStreak(payload) {
  const root = unwrap(payload);
  if (!root) return null;

  const reward = root.reward_status || {};
  const data =
    reward.data && typeof reward.data === "object" ? reward.data : {};
  const activeStreak = Math.max(
    0,
    toNumber(root.active_streak, toNumber(data.day, 0))
  );
  const eggsAwarded = Math.max(
    0,
    firstFinite(
      data.easters,
      reward.easters,
      root.eggs_awarded,
      root.easters,
      root.amount_to_claim
    ) ?? 0
  );
  const eggBalance = firstFinite(
    data.new_new_easters,
    data.new_easters,
    data.easters_balance,
    root.new_easters,
    root.easters
  );
  const success = root.success !== false && reward.success !== false;

  return {
    success,
    activeStreak,
    streakToClaim: activeStreak + 1,
    amountToClaim: eggsAwarded || toNumber(root.amount_to_claim, 0),
    rewardClaimed: success,
    displayDay: cycleDay(Math.max(activeStreak, 1)),
    nextDay: cycleDay(activeStreak + 1),
    eggBalance: eggBalance == null ? null : eggBalance,
    eggsAwarded,
    message: reward.message || root.message || "",
    allocatedOn: data.reward_allocated_on || null,
  };
}

/** Map absolute streak journey day → display label in the 1–30 cycle. */
function cycleStreakLabel(journeyDay) {
  const n = Math.max(1, toNumber(journeyDay, 1));
  return ((n - 1) % STREAK_CYCLE_LENGTH) + 1;
}

/**
 * 7-box streak window for intro + rewards UI.
 *
 * Days 1–5: active advances left → right (boxes 1–5).
 * Day 6+: active stays in box 5 (third-last); window slides.
 * Labels follow the claim counter (1–30), then repeat from 1.
 */
export function streakWeekDays(streak) {
  const rewardClaimed = Boolean(streak?.rewardClaimed);
  const focus = rewardClaimed
    ? Math.max(1, toNumber(streak?.activeStreak, 1))
    : Math.max(1, toNumber(streak?.streakToClaim, 1));

  // Day 1→box0 … Day 5→box4; Day 6+ stay on box 4 (third-last).
  const activeIndex = Math.min(focus, ACTIVE_LOCK_SLOT + 1) - 1;

  return Array.from({ length: WEEK_LENGTH }, (_, index) => {
    const journeyDay = focus + (index - activeIndex);
    const label = cycleStreakLabel(Math.max(1, journeyDay));
    const isCurrent = index === activeIndex;
    const isPast = index < activeIndex;
    const isFuture = index > activeIndex;

    return {
      day: label,
      dateKey: `streak-${journeyDay}-${label}`,
      claimed: isPast || (isCurrent && rewardClaimed),
      current: isCurrent,
      blank: isFuture,
      state: isFuture
        ? "locked"
        : isCurrent && !rewardClaimed
          ? "today"
          : "done",
    };
  });
}

export function persistStreakSession(streak) {
  if (!streak) return;

  let profile = {};
  try {
    profile = JSON.parse(sessionStorage.getItem("profileData") || "{}") || {};
  } catch {
    profile = {};
  }

  const next = {
    ...profile,
    active_streak: streak.activeStreak,
    login_streak: streak.activeStreak,
    loginStreak: streak.activeStreak,
    daily_streak: streak.activeStreak,
    streak: streak.activeStreak,
    streak_to_claim: streak.streakToClaim,
    amount_to_claim: streak.amountToClaim,
    rewardClaimed: streak.rewardClaimed,
  };

  if (streak.eggBalance != null) {
    next.easters = streak.eggBalance;
    next.eggs = streak.eggBalance;
    next.easter_eggs = streak.eggBalance;
    next.easterEggs = streak.eggBalance;
    sessionStorage.setItem("easterBalance", String(streak.eggBalance));
  }

  sessionStorage.setItem("profileData", JSON.stringify(next));
  sessionStorage.setItem("loginStreak", String(streak.activeStreak));
  sessionStorage.setItem(
    "ga_streak_reward_claimed",
    streak.rewardClaimed ? "1" : "0"
  );
  window.dispatchEvent(new Event("profileUpdated"));
}

export async function loadActiveStreak(options = {}) {
  const response = await getActiveStreak(options);
  const streak = normalizeActiveStreak(response?.data);
  if (streak) persistStreakSession(streak);
  return streak;
}

export async function claimDailyStreak() {
  const response = await updateDailyStreak();
  const streak = normalizeUpdatedStreak(response?.data);
  if (!streak?.success) return streak;

  invalidateCachedRequest(CACHE_KEYS.activeStreak);

  if (!hasRewardBaseline()) {
    try {
      const prior = await loadChallenges();
      rememberClaimedRewards(prior?.tasks);
    } catch (error) {
      console.error("Streak challenge snapshot failed:", error);
    }
  }

  const before = readEasterBalance();
  const awarded = Math.max(0, streak.eggsAwarded || streak.amountToClaim || 0);

  let loaded = null;
  try {
    loaded = await refreshChallenges();
  } catch (error) {
    console.error("Streak challenge sync failed:", error);
  }

  const afterRefresh = readEasterBalance();
  if (awarded > 0) {
    streak.eggBalance = persistEasterBalance(
      balanceAfterReward(before, awarded, afterRefresh)
    );
  } else if (streak.eggBalance != null) {
    streak.eggBalance = persistEasterBalance(streak.eggBalance);
  } else {
    streak.eggBalance = afterRefresh;
  }

  persistStreakSession(streak);
  announceStreakReward(streak, loaded);
  return streak;
}

function announceStreakReward(streak, loaded) {
  const awarded = Math.max(0, streak?.eggsAwarded || streak?.amountToClaim || 0);
  const task = (loaded?.tasks || []).find((item) => isLoginChallenge(item));
  const rewardEggs = Math.max(0, Number(task?.rewardEggs) || awarded);
  const shouldAnnounce =
    rewardEggs > 0 &&
    (awarded > 0 || (Boolean(task?.claimed) && claimedRewardIsNew(task)));

  if (!shouldAnnounce) return;

  announceEggReward({
    title: task?.title || "Claim Daily login / streak",
    rewardEggs,
    eggBalance: streak.eggBalance,
    taskKey: rewardTaskId(task) || "daily-login",
  });
}
